import * as React from 'react';
import { Box, Button, Grid, Typography, useTheme } from '@mui/material';
import BasicModal from '@components/BasicModal';
import BottomMarginContentWrapper from '@components/BottomMarginContentWrapper';
import EcChip from '@components/EcChip';
import PdfLoadingOverlay from '@components/PdfLoadingOverlay';
import { styled } from '@mui/system';
import { useBatchActionsContext } from '@common/context/batchActionsContext';
import { BatchActionSubject } from '@api/models/careerServicesApi.models';

const Styled = {
  InputLabel: styled(Typography)(({ theme }) => ({
    marginBottom: '5px',
    textTransform: 'uppercase',
    color: theme.palette.grey4.main
  })),
  BoxWrapper: styled(Box)(({ theme }) => ({
    display: 'flex',
    border: `0.5px solid ${theme.palette.OUTLINE.main}`,
    borderRadius: '5px',
    flexWrap: 'wrap',
    padding: '5px',
    gap: '5px',
    width: '100%',
    maxHeight: '304px',
    overflowY: 'scroll'
  }))
};

export interface SelectedRow {
  studentId: string;
  studentName: string;
  studentEmail: string;
  externshipId: number;
}

interface Props<T extends SelectedRow> {
  open: boolean;
  modalTitle: string;
  modalSubtext?: string;
  primaryBtnText: string;
  loading?: boolean;
  setSelectedRows: React.Dispatch<React.SetStateAction<T[]>>;
  handleClose: () => void;
  maxTagsToDisplay?: number;
  children?: React.ReactNode;
  handleSubmitClick: () => Promise<void>;
  batchActionSubjects: BatchActionSubject[];
  setBatchActionSubjects: React.Dispatch<React.SetStateAction<BatchActionSubject[]>>;
}

const BatchActionModalLayout = <T extends SelectedRow>({
  open,
  modalTitle,
  modalSubtext,
  primaryBtnText,
  loading = false,
  setSelectedRows,
  handleClose,
  maxTagsToDisplay = 10,
  children,
  handleSubmitClick,
  batchActionSubjects,
  setBatchActionSubjects,
}: Props<T>): React.ReactElement => {
  const theme = useTheme();

  const { rowSelectionModel } = useBatchActionsContext();
  const [showAll, setShowAll] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setShowAll(false);
    };
  }, [open]);

  React.useEffect(() => {
    if (open && !batchActionSubjects.length) {
      handleClose();
    };
  }, [open, batchActionSubjects]);

  const toDisplay = React.useMemo((): BatchActionSubject[] => {
    if (open && batchActionSubjects.length) {
      return showAll ? batchActionSubjects : batchActionSubjects.slice(0, maxTagsToDisplay);
    };

    return [];
  }, [open, showAll, batchActionSubjects]);

  const remaining = React.useMemo((): number => {
    if (open && batchActionSubjects.length && batchActionSubjects.length > maxTagsToDisplay) {
      return batchActionSubjects.length - maxTagsToDisplay;
    };
    return 0;
  }, [open, batchActionSubjects]);

  React.useEffect(() => {
    const rowData = rowSelectionModel.map((row) =>
      JSON.parse(row as string)
    ) as unknown as T[];

    setSelectedRows(rowData);
  }, [rowSelectionModel]);

  const handleShowAll = (): void => {
    setShowAll(true);
  };

  const handleRemove = (email: string): void => {
    setBatchActionSubjects((prev) => {
      return prev.filter((batchActionSubject) => {
        return batchActionSubject.email !== email;
      }
      );
    });
  };

  return (
    <>
      <BasicModal
        open={open}
        handleClose={handleClose}
        title={modalTitle}
        primaryAction={{
          label: primaryBtnText,
          action: handleSubmitClick
        }}
        secondaryAction={{ label: 'Cancel', action: handleClose }}
        maxWidth="sm"
      >
        <BottomMarginContentWrapper bottomMargin="24px">
          {modalSubtext && (
            <Typography variant="EC_TYPE_BASE">{modalSubtext}</Typography>
          )}
          <Styled.InputLabel variant="EC_TYPE_XS">TO:</Styled.InputLabel>
          <Styled.BoxWrapper>
            <Grid container spacing={1} sx={{ padding: '7px' }}>
              {toDisplay.map((displayItem, i) => {
                return (
                  <Grid item key={`${displayItem.email}-${i}`}>
                    <EcChip
                      dataTestId={`${displayItem.email}-${i}-test`}
                      label={displayItem.name}
                      handleDelete={() => {
                        handleRemove(displayItem.email);
                      }}
                      typography="EC_TYPE_2XS"
                      chipColor={theme.palette.GOLD_LIGHT.main}
                      labelColor={theme.palette.BLACK.main}
                      customSx={{
                        fontSize: '10px'
                      }}
                    />
                  </Grid>
                );
              })}
              {!!remaining && !showAll && (
                <Grid item>
                  <Button variant="text" onClick={handleShowAll}>
                    +{remaining} more...
                  </Button>
                </Grid>
              )}
            </Grid>
          </Styled.BoxWrapper>
          {children}
        </BottomMarginContentWrapper>
        <PdfLoadingOverlay
          loading={loading}
          dataTestId="assign-modal-loading-overlay"
        />
      </BasicModal>
    </>
  );
};

export default BatchActionModalLayout;
